import {
  collection,
  doc,
  deleteField,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Timestamp,
  Unsubscribe,
} from "firebase/firestore";
import { resolveCompanionDocumentProfileId } from "~/composables/useResolveCompanionDocumentProfileId";

export type CompanionProfile = {
  id: string;
  name: string;
  avatarHref: string;
  curriculumLevelId?: string;
  curriculumProductCategoryId?: string;
  curriculumUpdatedAtTimestamp?: Timestamp;
  licenseAssignmentId?: number;
  licenseId?: number;
  licenseName?: string;
  licenseStatus?: string;
  licenseActivationTime?: string | null;
  licenseExpirationTime?: string | null;
  licenseExpirationPeriodSeconds?: number | null;
  licenseAvailableToActivate?: boolean;
  licenseCurriculumLevelId?: string;
  licenseCurriculumSku?: string;
  licenseLinkedAtTimestamp?: Timestamp | number;
  createdAtTimestamp: Timestamp;
  updatedAtTimestamp: Timestamp;
  archivedAtTimestamp?: Timestamp;
};

export const useCompanion = defineStore("companion", () => {
  const profiles = ref<CompanionProfile[]>([]);
  const selectedProfileId = ref<string | undefined>();
  const isLoadingProfiles = ref(false);
  const isCreatingProfile = ref(false);
  const profilesSubscription = ref<Unsubscribe | undefined>();
  const hasInitializedProfiles = ref(false);

  const selectedProfile = computed(() => {
    return profiles.value.find(
      (profile) => profile.id === selectedProfileId.value
    );
  });

  const initializeProfiles = async () => {
    if (hasInitializedProfiles.value) return;

    isLoadingProfiles.value = true;

    const uid = useCurrentUID();

    if (!uid) {
      isLoadingProfiles.value = false;
      return;
    }

    hasInitializedProfiles.value = false;

    const db = useFirestore();
    const profilesRef = collection(db, `/users/${uid}/profiles`);
    const profilesQuery = query(
      profilesRef,
      orderBy("createdAtTimestamp", "asc")
    );

    profilesSubscription.value?.();

    await new Promise<void>((resolve) => {
      profilesSubscription.value = onSnapshot(
        profilesQuery,
        (snapshot) => {
          profiles.value = snapshot.docs
            .map((profileDoc) => {
              return {
                id: profileDoc.id,
                ...profileDoc.data(),
              } as CompanionProfile;
            })
            .filter((profile) => profile.archivedAtTimestamp == undefined);

          isLoadingProfiles.value = false;
          hasInitializedProfiles.value = true;
          resolve();
        },
        (error) => {
          isLoadingProfiles.value = false;
          hasInitializedProfiles.value = false;
          useBaseToast("Unable to load profiles.", "error");
          resolve();
        }
      );
    });
  };

  const selectProfile = (profileId: string) => {
    selectedProfileId.value = profileId;
  };

  const selectDocumentProfile = (profileId?: string) => {
    const resolvedProfileId = resolveCompanionDocumentProfileId(
      profileId,
      profiles.value
    );

    selectedProfileId.value = resolvedProfileId;
    return resolvedProfileId != undefined;
  };

  const createProfile = async ({
    name,
    avatarHref,
  }: {
    name: string;
    avatarHref: string;
  }) => {
    const uid = useCurrentUID();

    if (!uid) {
      useBaseToast("Please sign in before creating a profile.", "error");
      return;
    }

    const trimmedName = name.trim();

    if (!trimmedName) {
      useBaseToast("Please enter a profile name.", "error");
      return;
    }

    if (!avatarHref) {
      useBaseToast("Please choose an avatar.", "error");
      return;
    }

    isCreatingProfile.value = true;

    try {
      const db = useFirestore();
      const profileId = useGenerateUID();
      const profileRef = doc(db, `/users/${uid}/profiles/${profileId}`);
      const now = Timestamp.now();

      await setDoc(profileRef, {
        name: trimmedName,
        avatarHref,
        createdAtTimestamp: now,
        updatedAtTimestamp: now,
      });

      selectedProfileId.value = profileId;
    } finally {
      isCreatingProfile.value = false;
    }
  };

  const updateProfileCurriculum = async (levelId: string) => {
    const uid = useCurrentUID();
    const profileId = selectedProfileId.value;

    if (!uid || !profileId) {
      useBaseToast(
        "Please choose a profile before choosing curriculum.",
        "error"
      );
      return false;
    }

    const levelsStore = useLevels();

    if (levelsStore.allLevels.length === 0) {
      await levelsStore.initializeLevels();
    }

    const level = levelsStore.allLevels.find((level) => level.id === levelId);

    if (!level) {
      useBaseToast("Please choose a valid curriculum.", "error");
      return false;
    }

    const db = useFirestore();
    const now = Timestamp.now();
    const profileRef = doc(db, `/users/${uid}/profiles/${profileId}`);
    const profileUpdate: Record<string, unknown> = {
      curriculumLevelId: levelId,
      curriculumProductCategoryId: level.iewGradebookProductCategoryId,
      curriculumUpdatedAtTimestamp: now,
      updatedAtTimestamp: now,
    };
    const selectedProfileBeforeUpdate = selectedProfile.value;
    const currentLicenseSku = selectedProfileBeforeUpdate?.licenseCurriculumSku;
    const shouldClearLinkedLicense =
      !!currentLicenseSku && currentLicenseSku !== level.external_product_sku;

    if (shouldClearLinkedLicense) {
      profileUpdate.licenseAssignmentId = deleteField();
      profileUpdate.licenseId = deleteField();
      profileUpdate.licenseName = deleteField();
      profileUpdate.licenseStatus = deleteField();
      profileUpdate.licenseActivationTime = deleteField();
      profileUpdate.licenseExpirationTime = deleteField();
      profileUpdate.licenseExpirationPeriodSeconds = deleteField();
      profileUpdate.licenseAvailableToActivate = deleteField();
      profileUpdate.licenseCurriculumLevelId = deleteField();
      profileUpdate.licenseCurriculumSku = deleteField();
      profileUpdate.licenseLinkedAtTimestamp = deleteField();
    }

    await setDoc(profileRef, profileUpdate, { merge: true });

    profiles.value = profiles.value.map((profile) => {
      if (profile.id !== profileId) return profile;

      const updatedProfile = {
        ...profile,
        curriculumLevelId: levelId,
        curriculumProductCategoryId: level.iewGradebookProductCategoryId,
        curriculumUpdatedAtTimestamp: now,
        updatedAtTimestamp: now,
      };

      if (!shouldClearLinkedLicense) return updatedProfile;

      const profileWithoutLinkedLicense = { ...updatedProfile };
      delete profileWithoutLinkedLicense.licenseAssignmentId;
      delete profileWithoutLinkedLicense.licenseId;
      delete profileWithoutLinkedLicense.licenseName;
      delete profileWithoutLinkedLicense.licenseStatus;
      delete profileWithoutLinkedLicense.licenseActivationTime;
      delete profileWithoutLinkedLicense.licenseExpirationTime;
      delete profileWithoutLinkedLicense.licenseExpirationPeriodSeconds;
      delete profileWithoutLinkedLicense.licenseAvailableToActivate;
      delete profileWithoutLinkedLicense.licenseCurriculumLevelId;
      delete profileWithoutLinkedLicense.licenseCurriculumSku;
      delete profileWithoutLinkedLicense.licenseLinkedAtTimestamp;

      return profileWithoutLinkedLicense;
    });

    return true;
  };

  const renameProfile = async (name: string) => {
    const uid = useCurrentUID();
    const profileId = selectedProfileId.value;
    const trimmedName = name.trim();

    if (!uid || !profileId) {
      useBaseToast("Please choose a profile before renaming it.", "error");
      return false;
    }

    if (!trimmedName) {
      useBaseToast("Please enter a profile name.", "error");
      return false;
    }

    const now = Timestamp.now();
    const profileRef = doc(
      useFirestore(),
      `/users/${uid}/profiles/${profileId}`
    );

    await setDoc(
      profileRef,
      { name: trimmedName, updatedAtTimestamp: now },
      { merge: true }
    );

    profiles.value = profiles.value.map((profile) =>
      profile.id === profileId
        ? { ...profile, name: trimmedName, updatedAtTimestamp: now }
        : profile
    );

    return true;
  };

  const archiveProfile = async () => {
    const uid = useCurrentUID();
    const profileId = selectedProfileId.value;

    if (!uid || !profileId) {
      useBaseToast("Please choose a profile before deleting it.", "error");
      return false;
    }

    const now = Timestamp.now();
    const profileRef = doc(
      useFirestore(),
      `/users/${uid}/profiles/${profileId}`
    );

    await setDoc(
      profileRef,
      { archivedAtTimestamp: now, updatedAtTimestamp: now },
      { merge: true }
    );

    profiles.value = profiles.value.filter(
      (profile) => profile.id !== profileId
    );
    selectedProfileId.value = undefined;

    return true;
  };

  const resetProfileSelection = () => {
    selectedProfileId.value = undefined;
  };

  const reset = () => {
    profilesSubscription.value?.();
    profilesSubscription.value = undefined;
    profiles.value = [];
    selectedProfileId.value = undefined;
    hasInitializedProfiles.value = false;
    isLoadingProfiles.value = false;
    isCreatingProfile.value = false;
  };

  return {
    profiles,
    selectedProfileId,
    selectedProfile,
    isLoadingProfiles,
    isCreatingProfile,
    hasInitializedProfiles,
    initializeProfiles,
    selectProfile,
    selectDocumentProfile,
    createProfile,
    renameProfile,
    archiveProfile,
    updateProfileCurriculum,
    resetProfileSelection,
    reset,
  };
});
